<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Navbar></Navbar>
    <div class="profile-page">
      <el-card v-if="loadedUser" class="profile-card container-box" shadow="always">
        <div class="profile-info">
          <div class="profile-picture-container">
            <img :src="profilePictureUrl" alt="Profile Picture" class="profile-picture">
          </div>
          <div class="profile-details">
            <h2>
              {{ loadedUser.username }}
              <el-tag v-if="roleLabel" :type="loadedUser.status === 'ADMIN' ? 'danger' : 'success'" size="small">
                {{ roleLabel }}
              </el-tag>
            </h2>
            <p class="text-muted">Membre sur Wedoomotion depuis le {{ new Date(loadedUser.account_creation_date).toLocaleDateString('fr-FR') }}</p>
            <p v-if="isCurrentUser" class="text-muted">Adresse mail : {{ loadedUser.email }}</p>
          </div>
        </div>

        <div v-if="!isCurrentUser && sessionUserId" class="profile-actions">
          <el-button v-if="friendRequestSent" @click="cancelFriendRequest">Annuler la demande d'ami</el-button>
          <el-button v-else type="primary" @click="sendFriendRequest">Envoyer une demande d'ami</el-button>
          <el-button @click="openConversation">Envoyer un message</el-button>
          <ReportButton target-type="user" :target-id="resolvedUserId" size="default" />
        </div>
      </el-card>

      <el-card v-if="isCurrentUser" class="container-box" shadow="always">
        <el-button @click="isDataCollapsed = !isDataCollapsed">
          {{ isDataCollapsed ? 'Fermer' : 'Modifier mes informations utilisateur' }}
        </el-button>
        <el-form v-if="isDataCollapsed" label-position="top" class="edit-section" @submit.prevent="modifyUser">
          <el-form-item label="Nom d'utilisateur">
            <el-input v-model="newUsername" />
          </el-form-item>
          <el-form-item label="Mot de passe">
            <el-input v-model="newPassword" type="password" show-password />
          </el-form-item>
          <el-form-item label="Photo de profil">
            <input type="file" accept="image/*" name="profilePicture" @change="onProfilePictureChange">
          </el-form-item>
          <el-button type="primary" native-type="submit">Enregistrer mes modifications</el-button>
        </el-form>
      </el-card>

      <el-card v-if="loadedUser" class="container-box friends-box" shadow="always">
        <h3>Amis ({{ friends.length }})</h3>
        <ul v-if="friends.length" class="friends-list">
          <li v-for="friend in friends" :key="friend.id">
            <UserLink :username="friend.username" />
          </li>
        </ul>
        <p v-else class="text-muted">Aucun ami pour le moment.</p>
      </el-card>

      <el-card v-if="loadedUser" class="container-box interests-box" shadow="always">
        <h3>Centres d'intérêt</h3>
        <div v-if="userInterests.length" class="interest-tags">
          <el-tag v-for="interest in userInterests" :key="interest.id" effect="plain">{{ interest.name }}</el-tag>
        </div>
        <p v-else class="text-muted">Aucun centre d'intérêt renseigné.</p>
        <div v-if="isCurrentUser" class="interest-editor">
          <el-select
            v-model="selectedInterestIds"
            multiple
            filterable
            placeholder="Choisir mes centres d'intérêt"
            class="interest-select"
          >
            <el-option v-for="interest in allInterests" :key="interest.id" :label="interest.name" :value="interest.id" />
          </el-select>
          <el-button type="primary" @click="saveInterests">Enregistrer mes centres d'intérêt</el-button>
        </div>
      </el-card>

      <el-card v-if="loadedUser" class="container-box posts-box" shadow="always">
        <h3>Publications ({{ userPosts.length }})</h3>
        <ul v-if="userPosts.length" class="user-posts">
          <li v-for="post in userPosts" :key="post.id">
            <router-link :to="{ name: 'Post', params: { postId: post.id } }" class="user-post-link">
              <el-tag size="small" :type="post.type === 'ARTICLE' ? 'warning' : 'info'">
                {{ post.type === 'ARTICLE' ? 'Article' : 'Chippie' }}
              </el-tag>
              <span class="user-post-excerpt">{{ excerpt(post.content) }}</span>
            </router-link>
            <span class="text-muted user-post-meta">
              {{ new Date(post.createdAt).toLocaleDateString('fr-FR') }} · ❤️ {{ post.likeCount || 0 }}
            </span>
          </li>
        </ul>
        <p v-else class="text-muted">Aucune publication.</p>
      </el-card>

      <el-card v-if="canEditStatus" class="container-box" shadow="always">
        <el-button @click="isStatusCollapsed = !isStatusCollapsed">
          {{ isStatusCollapsed ? 'Fermer' : "Modifier le statut de l'utilisateur" }}
        </el-button>
        <el-form v-if="isStatusCollapsed" class="edit-section" @submit.prevent="modifyStatus">
          <el-form-item label="Statut">
            <el-radio-group v-model="newStatus">
              <el-radio v-if="sessionUserStatus === 'ADMIN'" value="ADMIN">Administrateur</el-radio>
              <el-radio value="MODERATOR">Modérateur</el-radio>
              <el-radio value="DEFAULT">Utilisateur par défaut</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-button type="primary" native-type="submit">Modifier le statut de {{ loadedUser.username }}</el-button>
        </el-form>
      </el-card>

      <!-- Droit d'accès et de portabilité (RGPD) : export JSON de ses propres données -->
      <el-card v-if="isCurrentUser" class="container-box" shadow="always">
        <el-button @click="downloadMyData">Télécharger mes données</el-button>
      </el-card>

      <el-card v-if="canDeleteAccount" class="container-box danger-box" shadow="always">
        <el-button type="danger" plain @click="confirmDeleteUser">
          {{ isCurrentUser ? 'Supprimer mon compte' : 'Supprimer ce compte' }}
        </el-button>
      </el-card>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import UserLink from '@/components/UserLink.vue';
import ReportButton from '@/components/ReportButton.vue';
import api, { API_BASE_URL } from '@/api';
import { notifySuccess, notifyError, confirmAction } from '@/notify';

export default {
  components: {
    Navbar,
    UserLink,
    ReportButton,
  },

  props: {
    username: String,
  },

  data() {
    return {
      sessionUserId: null,
      sessionUserStatus: null,
      resolvedUserId: null,
      loadedUser: null,
      friendRequestSent: false,
      friends: [],
      userInterests: [],
      allInterests: [],
      selectedInterestIds: [],
      userPosts: [],
      profilePicture: null,
      newUsername: '',
      newPassword: '',
      newStatus: '',
      isDataCollapsed: false,
      isStatusCollapsed: false,
    };
  },
  async created() {
    await this.loadSessionUser();
    await this.loadUser();
    // Inutile de vérifier une demande d'ami envers soi-même
    if (!this.isCurrentUser) {
      await this.checkFriendRequestStatus();
    }
    await this.loadFriends();
    await this.loadInterests();
    await this.loadUserPosts();
  },
  computed: {
    isCurrentUser() {
      // Faux tant que la session ou le profil ne sont pas chargés (deux ids null ne signifient pas « moi »)
      if (this.resolvedUserId == null || this.sessionUserId == null) return false;
      return Number(this.resolvedUserId) === Number(this.sessionUserId);
    },
    isAdmin() {
      return Number(this.resolvedUserId) !== Number(this.sessionUserId)
      && this.sessionUserStatus === 'ADMIN'
    },
    // ADMIN : tous les rôles. MODERATOR : DEFAULT <-> MODERATOR sur un non-admin
    // (le backend vérifie en plus qu'ils partagent un groupe d'entraide)
    canEditStatus() {
      if (!this.loadedUser || this.isCurrentUser) return false;
      if (this.sessionUserStatus === 'ADMIN') return true;
      return this.sessionUserStatus === 'MODERATOR' && this.loadedUser.status !== 'ADMIN';
    },
    // Seul le compte lui-même ou un ADMIN peut le supprimer (règle du backend)
    canDeleteAccount() {
      if (!this.loadedUser) return false;
      return this.isCurrentUser || this.isAdmin;
    },
    roleLabel() {
      if (!this.loadedUser) return '';
      if (this.loadedUser.status === 'ADMIN') return 'Administrateur';
      if (this.loadedUser.status === 'MODERATOR') return 'Modérateur';
      return '';
    },
    profilePictureUrl() {
      return this.loadedUser ? `${API_BASE_URL}/${this.loadedUser.profile_picture}` : '';
    },
  },

  mounted() {
    document.body.style.backgroundColor = 'rgba(21, 109, 123)';
  },

  methods: {
    async loadSessionUser() {
      try {
        const response = await api.get('/session/me', {
          withCredentials: true,
        });

        if (response.status === 200) {
          this.sessionUserId = response.data.id;
          this.sessionUserStatus = response.data.status;
        } else {
          console.error('Invalid session');
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    },

    async loadUser() {
      try {
        const response = await api.get(`/user/by-username/${this.username}`, { withCredentials: true });

        if (response.data.success) {
          this.loadedUser = response.data.user;
          this.resolvedUserId = response.data.user.id;
        } else {
          console.error('Failed to load user:', response.data.message);
        }
      } catch (error) {
        console.error('Error loading user:', error);
      }
    },

    // Appelée par l'<input type="file"> natif : le fichier est dans event.target.files
    onProfilePictureChange(event) {
      this.profilePicture = event.target.files[0] || null;
    },

    async modifyUser() {
      try {
        const formData = new FormData();

        if (this.newUsername) {
          formData.append('newUsername', this.newUsername);
        }

        if (this.newPassword) {
          formData.append('newPassword', this.newPassword);
        }

        if (this.profilePicture) {
          formData.append('profilePicture', this.profilePicture);
        }

        await api.post('/user/modify', formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        this.isDataCollapsed = false;

        //reloading the user after the modifications are done
        await this.loadUser();
        notifySuccess('Profil mis à jour');
      } catch (error) {
        console.error('Error modifying user:', error.response ? error.response.data.message : error.message);
        notifyError(error, 'Erreur lors de la modification du profil');
      }
    },

    // Delete user information method
    async deleteUser() {
      try {
        await api.delete(`/user/${this.loadedUser.id}`, {
          withCredentials: true,
        });

        // Ending the session
        this.loadedUser = null;
        this.sessionUserId = null;

        // Redirecting 
        if (this.isAdmin && !this.isCurrentUser){
          this.$router.back();
        } else {
          this.$router.push('/rubriques');
        }

        notifySuccess('Compte supprimé');

      } catch (error) {
        console.error("Error deleting user:", error.response ? error.response.data.message : error.message);
        notifyError(error, 'Erreur lors de la suppression du compte');
      }
    },

    // Télécharge l'export RGPD sous forme de fichier JSON
    async downloadMyData() {
      try {
        const response = await api.get('/user/me/export');
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'wedoomotion-mes-donnees.json';
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        notifyError(error, 'Erreur lors de l\'export des données');
      }
    },

    async confirmDeleteUser() {
      const confirmed = await confirmAction(
        "Êtes-vous sûr de vouloir supprimer ce compte ? Cette action est irréversible.",
        { title: 'Supprimer le compte', confirmText: 'Supprimer' }
      );

      if (confirmed) {
        this.deleteUser();
      }
    },

    async modifyStatus() {
      try {
        await api.patch(
          `/user/${this.resolvedUserId}/status`,
          { status: this.newStatus },
          { withCredentials: true }
        );

        this.isStatusCollapsed = false;
        notifySuccess('Statut mis à jour');
        // Recharge le profil pour refléter le nouveau rôle
        await this.loadUser();
      } catch (error) {
        console.error('Error modifying user:', error.response ? error.response.data.message : error.message);
        notifyError(error, 'Erreur lors de la modification du statut');
      }
    },

    async checkFriendRequestStatus() {
      if (!this.resolvedUserId) return;
      try {
        const response = await api.get(`/friend/checkFriendRequest/${this.resolvedUserId}`, {
          withCredentials: true,
        });

        this.friendRequestSent = response.data.friendRequestSent;
      } catch (error) {
        console.error('Error checking friend request status:', error.response ? error.response.data.message : error.message);
      }
    },

    async sendFriendRequest() {
      try {
        await api.post('/friend/sendRequest', {
          friendId: this.resolvedUserId,
        }, {
          withCredentials: true,
        });

        this.friendRequestSent = true;
        notifySuccess("Demande d'ami envoyée");
      } catch (error) {
        console.error('Error sending friend request:', error.response ? error.response.data.message : error.message);
        notifyError(error, "Impossible d'envoyer la demande d'ami");
      }
    },

    async cancelFriendRequest() {
      try {
        await api.post('/friend/deleteFriend', {
          friendId: this.resolvedUserId,
        }, {
          withCredentials: true,
        });

        this.friendRequestSent = false;
        notifySuccess("Demande d'ami annulée");
      } catch (error) {
        console.error('Error canceling friend request:', error.response ? error.response.data.message : error.message);
        notifyError(error, "Impossible d'annuler la demande d'ami");
      }
    },

    // Centres d'intérêt du profil ; pour son propre profil, la liste complète sert à l'édition
    async loadInterests() {
      if (!this.resolvedUserId) return;
      try {
        const response = await api.get(`/interest/getUserInterest/${this.resolvedUserId}`);
        this.userInterests = ((response.data && response.data.userInterests) || [])
          .map((link) => link.centerOfInterest)
          .filter(Boolean);
        this.selectedInterestIds = this.userInterests.map((interest) => interest.id);
      } catch (error) {
        console.error('Error loading interests:', error);
      }

      if (!this.isCurrentUser) return;
      try {
        const response = await api.get('/interest/getAllInterest');
        this.allInterests = (response.data && response.data.allInterests) || [];
      } catch (error) {
        console.error('Error loading all interests:', error);
      }
    },

    async saveInterests() {
      try {
        await api.post('/interest/selectInterest', { selectedInterests: this.selectedInterestIds });
        notifySuccess("Centres d'intérêt enregistrés");
        await this.loadInterests();
      } catch (error) {
        notifyError(error, "Erreur lors de l'enregistrement des centres d'intérêt");
      }
    },

    async loadUserPosts() {
      if (!this.resolvedUserId) return;
      try {
        const response = await api.get(`/post/getUserPost/${this.resolvedUserId}`);
        this.userPosts = (response.data && response.data.posts) || [];
      } catch (error) {
        console.error('Error loading user posts:', error);
      }
    },

    excerpt(text) {
      if (!text) return '';
      return text.length > 120 ? `${text.slice(0, 120)}…` : text;
    },

    openConversation() {
      this.$router.push({ name: 'Conversation', params: { userId: this.resolvedUserId } });
    },

    // Amis acceptés de l'utilisateur affiché : chaque relation contient les deux
    // utilisateurs (user / friend), on garde celui qui n'est pas le profil consulté
    async loadFriends() {
      if (!this.resolvedUserId) return;
      try {
        const response = await api.get(`/friend/userFriends/${this.resolvedUserId}`, {
          withCredentials: true,
        });
        const relations = (response.data && response.data.userFriends) || [];
        this.friends = relations
          .map((relation) => (Number(relation.user && relation.user.id) === Number(this.resolvedUserId)
            ? relation.friend
            : relation.user))
          .filter(Boolean);
      } catch (error) {
        console.error('Error loading friends:', error);
      }
    },
  },
};
</script>

<style scoped>
.profile-page {
  padding: 70px 20px 20px;
  max-width: 760px;
  margin: 0 auto;
}

.container-box {
  margin-bottom: 16px;
}

.container-box h3 {
  margin: 0 0 10px 0;
  color: #303133;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.profile-details h2 {
  margin: 0 0 8px 0;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-actions {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.text-muted {
  color: #606266;
  margin: 0 0 8px 0;
}

.edit-section {
  margin-top: 16px;
}

.profile-picture-container {
  width: 150px;
}

.profile-picture {
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 50%;
}

.friends-list,
.user-posts {
  list-style: none;
  padding: 0;
  margin: 0;
}

.friends-list li {
  padding: 4px 0;
}

.interest-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.interest-editor {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.interest-select {
  flex: 1;
  min-width: 220px;
}

.user-posts li {
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
}

.user-post-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
  text-decoration: none;
}

.user-post-link:hover .user-post-excerpt {
  text-decoration: underline;
}

.user-post-meta {
  font-size: 12px;
}
</style>
